import { useState, useEffect } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as CodePipelinesService from '../services/CodePipelinesService';
import { notifyError, notifySuccess } from '../../../../components/PlatformNotification';

// Query keys
export const codePipelinesKeys = {
    all: ['codePipelines'] as const,
    lists: () => [...codePipelinesKeys.all, 'list'] as const,
    list: () => [...codePipelinesKeys.lists()] as const,
    details: () => [...codePipelinesKeys.all, 'detail'] as const,
    detail: (name: string) => [...codePipelinesKeys.details(), name] as const,
};

// Module-scoped filter store (global-filter-store-pattern). The Filter button,
// the drawer and the list hook all read this without prop drilling.
//
// The filters are NOT in the query key: `listCodePipelines` takes no filter
// argument, so filtering happens client-side over the pages already fetched.
// Keying on them would refetch identical data under a different cache entry.
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useCodePipelinesFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};

// Hooks
export const useCodePipelinesQuery = () => {
    return useInfiniteQuery({
        queryKey: codePipelinesKeys.list(),
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            CodePipelinesService.fetchRecords({ nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
        staleTime: 30_000,
    });
};

export const useCodePipelineQuery = (name: string) => {
    return useQuery({
        queryKey: codePipelinesKeys.detail(name),
        queryFn: () => CodePipelinesService.fetch(name),
        enabled: !!name,
        staleTime: 30_000,
    });
};

export const useDeleteCodePipelineMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: CodePipelinesService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Code pipeline deleted');
            queryClient.invalidateQueries({ queryKey: codePipelinesKeys.all });
        },
        onError: notifyError,
    });
};

export const useImportCodePipelineMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: CodePipelinesService.importPipeline,
        onSuccess: () => {
            notifySuccess('Code pipeline imported');
            queryClient.invalidateQueries({ queryKey: codePipelinesKeys.all });
            queryClient.invalidateQueries({ queryKey: ['pipelines'] });
        },
        onError: notifyError,
    });
};

export const useImportAllCodePipelinesMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: CodePipelinesService.importAll,
        onSuccess: () => {
            notifySuccess('All code pipelines imported');
            queryClient.invalidateQueries({ queryKey: codePipelinesKeys.all });
        },
        onError: notifyError,
    });
};

export const useMigrateCodePipelineMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: CodePipelinesService.migratePipeline,
        onSuccess: () => {
            notifySuccess('Code pipeline migrated');
            queryClient.invalidateQueries({ queryKey: codePipelinesKeys.all });
        },
        onError: notifyError,
    });
};
